import {
  alpha,
  Box,
  Stack, Tooltip,
  Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  OverlayDivider,
  OverlayGroupStack,
  OverlayStack,
  OverlayTypography,
  CtaOverlayIcon
} from "./CtaOverlay.styles";
import { CtaOverlayProps } from "./CtaOverlay.types";
import {
  CreateContentModal,
  Link,
  Spinner,
  StyledSnackBarFavourite
} from "../../components";
import { POST_TYPE } from "../../consts";
import {
  useFavouritesMutation,
  // useFavouritesQuery,
  useRatingMutation,
  useRatingQuery
} from "../../queries";
import { useAuthStore, useCommunityClusterStore, useCurrentCommunityCluster } from "../../stores";
import type { LearningObject } from "../../types";
import { getLearningObjectType } from "../../utils/general";
import { Icon } from "../Icon";

export function CtaOverlay({
  // bookmarked,
  // comments,
  courseId,
  learningObjectTypology,
  id,
  isStandAlone,
  // favourite,
  //share,
  rootId,
  sxOverlaySpan,
  rating,
  isHovered,
  isFavourite
} : CtaOverlayProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isLoading = useAuthStore(state => state.isLoading);
  const smartConfiguration = useAuthStore(state => state.smartConfiguration);
  const [visibilityRating, setVisibilityRating] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number|undefined>(undefined);
  const [commentsModalIsOpen, setCommentModalIsOpen] = useState(false);
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [ariaMessage, setAriaMessage] = useState("");
  const learningObjectType = getLearningObjectType(learningObjectTypology);
  const anchorElRef = useRef<HTMLDivElement | null>(null);
  const elAnchor = anchorElRef?.current as HTMLElement;
  const { t } = useTranslation();

  // const { data: favouriteStatistic } = useFavouritesQuery({
  //   enabled: Boolean(id),
  //   learningObjectId: id ?? "",
  //   learningObjectType
  // });

  // TO ADD IN PARAMS IN FAVOURITES
  const parentId = !isStandAlone ? (!courseId && rootId) ? rootId : courseId ?? null : null;
  const grandParentId = (!isStandAlone && parentId && parentId !== rootId) ? rootId : null ;
  const {
    data: ratingStatistic,
    isLoading: ratingIsLoading
  } = useRatingQuery({
    enabled: Boolean(id) && (isHovered ?? true),
    learningObjectId: id ?? "",
    learningObjectTypology:learningObjectType
  });
  const {
    mutate: addFavouriteMutation,
    isSuccess: isFavouriteAdded,
    isLoading: isAddingFavouriteLoading
  } = useFavouritesMutation({
    grandParentId:grandParentId,
    learningObjectId: id ?? "",
    learningObjectTypology,
    method: "POST",
    parentId:parentId
  });
  const {
    mutate: removeFavouriteMutation,
    isSuccess: isFavouriteRemoved,
    isLoading: isRemovingFavouriteLoading
  } = useFavouritesMutation({
    learningObjectId: id ?? "",
    learningObjectTypology,
    method: "DELETE"
  });

  const loStatistic = {
    averageRating: ratingStatistic?.objectStatistics.averageRating || 0,
    userFavourite: ratingStatistic 
      ? Boolean(ratingStatistic?.userRating.isFavouriteForLearner && ratingStatistic?.objectStatistics?.learningObjectId)
      : Boolean(isFavourite),
    userRating: ratingStatistic?.userRating?.rating || 0
  };
  const { availableClusters, currentCluster } = useCurrentCommunityCluster();
  const { setCurrentCluster } = useCommunityClusterStore();
  const sharingEnabled = !!availableClusters.find((cluster) => cluster.is_share_enabled);
  if(!currentCluster?.is_share_enabled && sharingEnabled) {
    setCurrentCluster(availableClusters.find((cluster) => cluster.is_share_enabled));
  }


  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    // Handle added favourite
    if (isFavouriteAdded && loStatistic.userFavourite) {
      setSnackbarVisible(true);
      setAriaMessage(t("has_been_added"));
      timer = setTimeout(() => {
        setSnackbarVisible(false);
      }, 5000);
    }

    // Handle removed favourite
    else if (isFavouriteRemoved && !loStatistic.userFavourite) {
      setSnackbarVisible(true);
      setAriaMessage(t("has_been_removed"));
      timer = setTimeout(() => {
        setSnackbarVisible(false);
      }, 5000);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [
    isFavouriteAdded,
    isFavouriteRemoved,
    loStatistic.userFavourite
  ]);


  return (
    <>
      {
        (
          (smartConfiguration?.funcFavourites || smartConfiguration?.funcRating ||
            (sharingEnabled) )
          && (!ratingIsLoading && !isLoading)
        ) ?
          (

            <OverlayStack
              className="overlaystack"
              component={ "span" }
              gap={ 2 }
              sx={ sxOverlaySpan }
              ref={ anchorElRef  }
              $commentModalOpen={ commentsModalIsOpen }
            >
              {
                // TODO: add smart conf check for comments
                // eslint-disable-next-line no-constant-condition
                smartConfiguration?.funcRating ||
                (sharingEnabled)
                  ? (
                    <OverlayGroupStack
                      divider={
                        <OverlayDivider
                          aria-hidden="true"
                          orientation={ isMobile ? "vertical" : "horizontal" }
                        />
                      }
                      gap={ isMobile ? 0 : 0 }
                    >
                      {
                        sharingEnabled ?
                          <Tooltip title={ t("community") } placement={ isMobile ? "bottom" : "left" } componentsProps={ {
                            popper: {
                              sx: {
                                "&[data-popper-placement*=\"bottom\"] .MuiTooltip-tooltip": {
                                  marginTop: "6px !important"
                                }
                              }
                            }
                          } }>
                            <Stack
                              id="item-0"
                              role="button"
                              tabIndex={ 0 }
                              aria-current={ activeIndex === 0 }
                              aria-label={ t("share_comment") }
                              onFocus={ () => setActiveIndex(0) }
                              onBlur={ () => setActiveIndex(undefined) }
                              onMouseEnter={ () => { setActiveIndex(0) } }
                              onMouseLeave={ () => { setActiveIndex(undefined) } }
                              sx={ {
                                ...(activeIndex === 0 && {
                                  backgroundColor: theme.customColors.backgroundSecondaryCtaHover,
                                  borderRadius: theme.spacing(1),
                                  color: theme.customColors.textPrimary,
                                  "&:not(:last-child)": {
                                    [theme.breakpoints.down("sm")]: {
                                      borderTopRightRadius: 0,
                                      borderBottomRightRadius: 0
                                    },
                                    [theme.breakpoints.up("sm")]: {
                                      borderBottomLeftRadius: 0,
                                      borderBottomRightRadius: 0
                                    }
                                  },
                                  "& .onhover_ctaoverlay": {
                                    "path":{
                                      fill: theme.customColors.textPrimary,
                                      stroke: theme.customColors.textPrimary
                                    }
                                  },
                                  "& .onhover_ctaoverlay_typography":{
                                    color:theme.customColors.textPrimary
                                  },
                                  "path":{
                                    fill: theme.customColors.textPrimary,
                                    stroke: theme.customColors.textPrimary
                                  }
                                }),
                                ":hover":{
                                  backgroundColor: theme.customColors.backgroundSecondaryCtaHover,
                                  borderRadius: theme.spacing(1),
                                  "&:not(:last-child)": {
                                    [theme.breakpoints.down("sm")]: {
                                      borderTopRightRadius: 0,
                                      borderBottomRightRadius: 0
                                    },
                                    [theme.breakpoints.up("sm")]: {
                                      borderBottomLeftRadius: 0,
                                      borderBottomRightRadius: 0
                                    }
                                  },
                                  "path":{
                                    fill: theme.customColors.textPrimary,
                                    stroke: theme.customColors.textPrimary
                                  }
                                },
                                height: "100%",
                                maxWidth: "100%"
                              } }
                              alignItems="center"
                              onClick={ () => { setCommentModalIsOpen(true) } }
                              onKeyDown={ (e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  setCommentModalIsOpen(true);
                                }
                              } }
                            >
                              <CtaOverlayIcon
                                color={ theme.customColors.textPrimary }
                                aria-hidden="true"
                                className="withpadding onhover_ctaoverlay"
                                icon={ "icon_share" } size={ 24 }/>
                            </Stack>
                          </Tooltip> : null
                      }

                      {
                        smartConfiguration?.funcRating ? (
                          <Box
                            position={ "relative" }
                            id="button-valutazione"
                            role="group"
                            tabIndex={ 0 }
                            aria-current={ activeIndex === 1 }
                            aria-labelledby="overlay-rating-1-to-5"
                            aria-label={ t("rating_btn_description") }
                            aria-controls="item-2a"
                            onFocus={ () => { setActiveIndex(1); setVisibilityRating(true)} }

                            onBlur={ () => {
                              setActiveIndex(undefined);
                              setVisibilityRating(false);
                            } }
                            onMouseEnter={ ()=> {
                              setActiveIndex(1);
                              setVisibilityRating(true);
                            } }
                            onMouseLeave={ ()=> {
                              setVisibilityRating(false);
                              setActiveIndex(undefined);
                            } }
                            onKeyDown={ (e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setVisibilityRating((prev) => !prev);
                                setActiveIndex((prev:number|undefined) => (prev === 1 ? undefined : 1));
                              }
                            } }
                            sx={ {
                              color: theme.customColors.textPrimary,
                              marginTop: 0,
                              paddingTop: "5px",
                              "&:before":{
                                background: "transparent",
                                content: "''",
                                display: "block",
                                height: "36px",
                                width: "12px",

                                pointerEvents: visibilityRating ? "auto" : "none",
                                position: "absolute",
                                right: "48px",

                                [`${theme.breakpoints.down("sm")}`]:{
                                  bottom:"40px",
                                  right: "23px",

                                  height: "12px",
                                  width: "36px"
                                }
                              },
                              ...(activeIndex === 1 && {
                                backgroundColor: theme.customColors.backgroundSecondaryCtaHover ,
                                borderRadius: theme.spacing(1),
                                color: theme.customColors.textPrimary,
                                "&:not(:last-child)": {
                                  [theme.breakpoints.down("sm")]: {
                                    borderTopRightRadius: 0,
                                    borderBottomRightRadius: 0
                                  },
                                  [theme.breakpoints.up("sm")]: {
                                    borderBottomLeftRadius: 0,
                                    borderBottomRightRadius: 0
                                  }
                                },
                                "& .onhover_ctaoverlay": {
                                  "path":{
                                    fill: theme.customColors.textPrimary,
                                    stroke: theme.customColors.textPrimary
                                  }
                                },
                                "& .onhover_ctaoverlay_typography":{
                                  color:theme.customColors.textPrimary
                                },
                                "path":{
                                  fill: theme.customColors.textPrimary,
                                  stroke: theme.customColors.textPrimary
                                }
                              }),
                              ":hover":{
                                backgroundColor: theme.customColors.backgroundSecondaryCtaHover,
                                borderRadius: theme.spacing(1),
                                "&:not(:first-child)": {
                                  [theme.breakpoints.down("sm")]: {
                                    borderTopLeftRadius: 0,
                                    borderBottomLeftRadius: 0
                                  },
                                  [theme.breakpoints.up("sm")]: {
                                    borderTopLeftRadius: 0,
                                    borderTopRightRadius: 0
                                  }
                                },
                                "& .onhover_ctaoverlay": {
                                  "path":{
                                    fill: theme.customColors.textPrimary,
                                    stroke: theme.customColors.textPrimary
                                  }
                                },
                                "& .onhover_ctaoverlay_typography":{
                                  color:theme.customColors.textPrimary
                                }
                              }

                            } }
                          >
                            <StarsIcons
                              id={ id ?? "" }
                              userRating={ loStatistic.userRating }
                              learningObjectTypology={ learningObjectTypology }
                              visibility={ visibilityRating }
                              maxRating = { smartConfiguration?.ratingMax || 1 }
                            />
                            <Tooltip
                              aria-labelledby="overlay-rating-1-to-5"
                              title={ t("rating") }
                              placement={ isMobile ? "bottom" : "left" }
                              componentsProps={ {
                                popper: {
                                  sx: {
                                    inset: isMobile ? "0px 0px auto auto" : "25px 0px auto auto !important",
                                    "&[data-popper-placement*=\"bottom\"] .MuiTooltip-tooltip": {
                                      marginTop: "14px !important"
                                    },
                                    "&[data-popper-placement*=\"left\"] .MuiTooltip-tooltip": {
                                      position: "relative !important"
                                    }
                                  }
                                }
                              } }
                            >
                              <Stack
                                alignItems="center"
                                flexDirection={ isMobile ? "row" : "column" }
                                marginLeft={ isMobile ? "10px" : "0" }
                                marginRight={ isMobile ? "10px" : 0 }>
                                <CtaOverlayIcon
                                  color={ theme.customColors.textPrimary }
                                  // color={ theme.customColors.accentVariantA }
                                  aria-hidden="true"
                                  className="star onhover_ctaoverlay"
                                  icon={ getStarIcon({ rating }) } size={ 30 } sx={ { paddingTop: "3px" } }
                                />
                                <OverlayTypography
                                  color={ theme.customColors.textPrimary }
                                  id="overlay-rating-1-to-5"
                                  className="onhover_ctaoverlay_typography"
                                  paddingBottom={ isMobile ? "0" : "12px" }
                                  paddingRight={ isMobile ? "5px" : 0 }
                                  marginTop={ 0 }
                                >
                                  { `${loStatistic?.averageRating ?? rating}/${smartConfiguration.ratingMax}` }
                                </OverlayTypography>
                              </Stack>
                            </Tooltip>
                          </Box>
                        ) : null
                      }
                    </OverlayGroupStack>
                  ) : null
              }
              {
                smartConfiguration?.funcFavourites && ((!rootId || learningObjectTypology === "PATH") && (!courseId  || learningObjectTypology === "COURSE")) ? (
                  <OverlayGroupStack
                    divider={ <OverlayDivider/> }
                  >
                    <Tooltip title={ loStatistic.userFavourite ? t("remove_from_favourites") : t("add_to_favourites") } placement={ isMobile ? "bottom" : "left" }
                      componentsProps={ {
                        popper: {
                          sx: {
                            "&[data-popper-placement*=\"bottom\"] .MuiTooltip-tooltip": {
                              marginTop: "6px !important"
                            }
                          }
                        }
                      } }
                    >
                      <Stack
                        id="item-2"
                        role="button"
                        tabIndex={ 0 }
                        aria-current={ activeIndex === 2 }
                        onFocus={ () =>  setActiveIndex(2) }
                        onBlur={ () =>  setActiveIndex(undefined) }
                        onMouseEnter={ ()=> {
                          setActiveIndex(2);
                        } }
                        onMouseLeave={ ()=> {
                          setActiveIndex(undefined);
                        } }
                        aria-label={ loStatistic.userFavourite ? t("remove_from_favourites") : t("add_to_favourites") }
                        onClick={ () => {
                          loStatistic.userFavourite ? removeFavouriteMutation() : addFavouriteMutation();
                        } }
                        onKeyDown={ (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            loStatistic.userFavourite ? removeFavouriteMutation() : addFavouriteMutation();
                          }
                        } }
                        sx={ {
                          ...(activeIndex === 2 && {
                            borderRadius: "0.5rem",
                            "& .onhover_ctaoverlay": {
                              // "path":{
                              //   fill: theme.customColors.systemSecondary05,
                              //   stroke: theme.customColors.systemSecondary05 && alpha(theme.customColors.systemSecondary05, 0.6)
                              // }
                            }
                            // "& .onhover_ctaoverlay_typography":{
                            //   color:theme.customColors.systemSecondary05
                            // }
                          }),
                          ":hover":{
                            backgroundColor:theme.customColors.backgroundSecondaryCtaHover,
                            borderRadius: "0.5rem"
                          }
                        } } alignItems="center">
                        {

                          (isAddingFavouriteLoading || isRemovingFavouriteLoading || (isHovered && ratingIsLoading) ) ?
                            <Spinner size={ 24 } padding={ 0 }/> :
                            (loStatistic.userFavourite) ? (
                              <CtaOverlayIcon
                                color={ theme.customColors.textPrimary }
                                aria-hidden="true"
                                className="withpadding onhover_ctaoverlay"
                                icon={ "Icons_salva-preferito_glow" }
                                size={ 24 } />
                            ): (
                              <CtaOverlayIcon
                                color={ theme.customColors.textPrimary }
                                aria-hidden="true"
                                icon={ "plus" }
                                className="withpadding onhover_ctaoverlay"
                                size={ 24 } />
                            )
                        }
                        <Box
                          aria-live="polite"
                          sx={ {
                            position: "absolute",
                            width: 1,
                            height: 1,
                            overflow: "hidden",
                            clip: "rect(0 0 0 0)",
                            whiteSpace: "nowrap"
                          } }
                        >
                          { ariaMessage }
                        </Box>
                      </Stack>
                    </Tooltip>
                  </OverlayGroupStack>
                ) : null
              }
              <StyledSnackBarFavourite
                $visible={ snackbarVisible }
              >
                <Typography
                  aria-hidden="true"
                  color={ theme.customColors.textPrimary }
                  fontSize={ theme.spacing(1.625) }
                  fontWeight={ 500 }
                >
                  { loStatistic.userFavourite  ? t("item_added_favourites") : t("item_removed_favourites") }
                  <Link
                    aria-hidden="true"
                    tabIndex={ -1 }
                    onClick={ (e) => {
                      e.stopPropagation();
                    } }
                    href="/bookmarks" fontWeight={ 700 }>
                    { " " + t("favourites") }
                  </Link>
                </Typography>
              </StyledSnackBarFavourite>
            </OverlayStack>
          ) : null
      }
      {
        commentsModalIsOpen ?

          <CreateContentModal
            selectedFilter={ null }
            selectedTopic={ null }
            anchorPosition={ {
              left:0,
              top:isMobile ? 200 : 0
            } }
            anchorOrigin={ {
              horizontal: -24,
              vertical: "top"
            } }
            transformOrigin={ {
              horizontal: "right",
              vertical: isMobile ? "bottom" : "top"
            } }
            anchorEl={ elAnchor }
            closeDrawer={ () => setCommentModalIsOpen(false) }
            onClose={ () => {setCommentModalIsOpen(false)} }
            open={ commentsModalIsOpen }
            postData={ {
              loAuthor: "abc123",
              loId: id.toString(),
              loType: learningObjectTypology
            } }
            postType={ POST_TYPE.SHARED }
          /> : null
      }

    </>
  );

  function getStarIcon({ rating } : Pick<CtaOverlayProps, "rating">) {
    if (rating <= 0 || (rating > (smartConfiguration ? smartConfiguration?.ratingMax : 1) )) {
      return "Icons_rating_active";
    }
    const grade = rating / (smartConfiguration ? smartConfiguration?.ratingMax : 1);
    if (grade === 1) {
      return "Icons_rating-1";
    } else if (grade < 0.1) {
      return "Icons_rating_active";
    } else {
      return "Star3-5";
    }
  }
}

function StarsIcons({
  id,
  learningObjectTypology,
  maxRating,
  userRating,
  visibility
} : Pick<
    LearningObject,
    | "id"
    | "learningObjectTypology"
  > & {
  maxRating:number
  userRating: number
  visibility: boolean
}) {
  const theme = useTheme();
  const [selectedStar, setSelectedStar] = useState<number>(userRating);
  const [hoveredStar, setHoveredStar] = useState<number| null>(null);
  const iconsRating = getIconsRating(maxRating);
  const learningObjectType = getLearningObjectType(learningObjectTypology);

  const { mutate: addRating } = useRatingMutation({
    learningObjectId: id.toString(),
    learningObjectType
  });

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      id="item-2a"
      role="group"
      aria-label={ t("rating_btn") }
      aria-hidden={ !visibility }
      aria-live={ visibility ? "polite" : "off" }
      display={ "flex" }
      flexDirection={ "row-reverse" }
      gap={ 1 }
      padding={ theme.spacing(1, 1.5) }
      position={ "absolute" }
      right={ isMobile ? "unset" : "60px" }
      width={ "fit-content" }
      sx={ {
        alignItems: "center",
        backgroundColor: theme.customColors.backgroundPrimary ,
        borderRadius: "0.5rem",
        boxShadow: `0.25rem 0.25rem 0.75rem ${theme.palette.common?.black && alpha(theme.palette.common?.black, 0.25)}`,
        opacity: visibility ? 1 : 0,
        pointerEvents: visibility ? "auto" : "none",

        [theme.breakpoints.down("sm")]: {
          left:"0px",
          top: "-36px",
          borderRadius: "0.5rem",
          backgroundColor: theme.customColors.backgroundPrimaryCtaHover
        }

      } }
    >
      {
        iconsRating.map((icon, index) => {
          return <Stack
            key={ `star-${index.toString()}` }
            id={ `item-star-${index}` }
            role="button"
            tabIndex={ visibility ? 0 : -1 }
            aria-current={ selectedStar === icon.point }
            aria-label={ t("select_star") + `${index+1}` }
            sx={ {
              ".icon path": {
                transform:isMobile ?
                  ( (hoveredStar && hoveredStar <= icon.point)
                    ? "scale(1.6) translate(-190px, -190px)"
                    : (!hoveredStar && selectedStar && selectedStar <= icon.point)
                      ? "scale(1.6) translate(-190px, -190px)"
                      : undefined) :
                  ( (hoveredStar && hoveredStar >= icon.point)
                    ? "scale(1.6) translate(-190px, -190px)"
                    : (!hoveredStar && selectedStar && selectedStar >= icon.point)
                      ? "scale(1.6) translate(-190px, -190px)"
                      : undefined)
              } }
            }
            onClick={ () => {
              let point = icon.point;
              if(isMobile){
                point = iconsRating.length - 1 - (index - 1);
              }
              addRating({ rating: point });
              setSelectedStar(icon.point);
            } }
            onMouseOver={ ()=> {
              setHoveredStar(icon.point);
            }
            }
            onMouseLeave={ ()=>{
              setHoveredStar(null);
            } }
            onFocus={ () => setHoveredStar(icon.point) }
            onBlur={ () => setHoveredStar(null) }
            onKeyDown={ (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                let point = icon.point;
                if(isMobile){
                  point = iconsRating.length - 1 - (index - 1);
                }
                addRating({ rating: point });
                setSelectedStar(icon.point);
              }
            } }
          >
            <Icon
              aria-hidden="true"
              icon={ isMobile ?
                ((hoveredStar && hoveredStar <= icon.point)
                  ? "Icons_rating-1"
                  : (!hoveredStar && selectedStar && selectedStar <= icon.point)
                    ? "Icons_rating-1"
                    : "Icons_rating") :  ((hoveredStar && hoveredStar >= icon.point)
                  ? "Icons_rating-1"
                  : (!hoveredStar && selectedStar && selectedStar >= icon.point)
                    ? "Icons_rating-1"
                    : "Icons_rating")
              }
              size={ 24 }
              color={ theme.customColors.textPrimary }
            />
          </Stack>;
        })
      }
      <Box
        aria-live="polite"
        sx={ {
          position: "absolute",
          width: 1,
          height: 1,
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
          whiteSpace: "nowrap"
        } }
      >
        { selectedStar ? `${t("rated_given")} ${selectedStar} ${selectedStar > 1 ? t("stars") : t("star") }` : "" }
      </Box>
    </Box>
  );

  function getIconsRating(ratingMax:number) {

    const iconArray  = Array.from({ length: ratingMax }, (key, index) => ({
      icon: "Icons_rating",
      point: index + 1
    }));
    return iconArray;
  }
}
